/** Aufgaben (E10.2): manuell und automatisch (Mietende, Rückgabe-Kiste, Warteliste); zuweisen, fällig, erledigen. Kasse zeigt die offenen für die Schicht. */
import { useState, type FormEvent } from "react";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatDatumZeit, formatTag, tagVon } from "@shared/zeit";
import { usePersonal } from "./stammdaten-api";
import { useAufgaben, useAufgabenAktion } from "./alltag-api";

const ART_TEXT: Record<string, string> = { manuell: "Aufgabe", mietende: "Mietende", rueckgabe_kiste: "Rückgabe-Kiste", warteliste: "Warteliste", etiketten: "Etiketten" };

export function Aufgaben() {
  const [nur, setNur] = useState<"offen" | "erledigt">("offen");
  const q = useAufgaben(nur);
  const personal = usePersonal();
  const aktion = useAufgabenAktion();
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ titel: "", text: "", zugewiesenBenutzerId: "", faelligTag: "" });
  const [meldung, setMeldung] = useState<string | null>(null);
  const heute = tagVon();
  const personen = personal.data?.personen.filter((p) => p.aktiv) ?? [];

  async function anlegen(e: FormEvent) {
    e.preventDefault();
    if (form.titel.trim().length < 2) return;
    try {
      await aktion.mutateAsync({ art: "anlegen", daten: { titel: form.titel.trim(), text: form.text.trim(), zugewiesenBenutzerId: form.zugewiesenBenutzerId || null, faelligTag: form.faelligTag || null } });
      setOffen(false);
      setForm({ titel: "", text: "", zugewiesenBenutzerId: "", faelligTag: "" });
    } catch (err) {
      setMeldung(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt.");
    }
  }

  return (
    <>
      <PageHeader kicker="Personal" title="Aufgaben" sub="Was im Laden zu tun ist – automatisch bei Mietende und voller Rückgabe-Kiste, oder von Hand" actions={<span className="sc-toolbar"><Button variant="secondary" icon="undo-2" loading={aktion.isPending && aktion.variables?.art === "automatik"} onClick={() => aktion.mutate({ art: "automatik" }, { onSuccess: (r) => setMeldung(`${(r as { neu: number }).neu} neue automatische Aufgaben.`) })}>Automatik prüfen</Button><Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Aufgabe</Button></span>} />
      {meldung ? <Notice tone="info">{meldung}</Notice> : null}
      <div className="sc-toolbar" style={{ marginBottom: "var(--space-4)" }}>
        <button type="button" className="sc-pill" aria-pressed={nur === "offen"} onClick={() => setNur("offen")}>Offen</button>
        <button type="button" className="sc-pill" aria-pressed={nur === "erledigt"} onClick={() => setNur("erledigt")}>Erledigt</button>
      </div>
      <Seitenzustand query={q} lines={6} istLeer={(d) => d.aufgaben.length === 0} leer={<Card><EmptyState compact illustration="all-done" title={nur === "offen" ? "Nichts offen" : "Noch nichts erledigt"}>{nur === "offen" ? "Automatische Aufgaben entstehen bei Mietende und voller Rückgabe-Kiste." : "Erledigte Aufgaben erscheinen hier."}</EmptyState></Card>}>
        {(d) => (
          <Card flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead><tr><th /><th>Aufgabe</th><th>Art</th><th>Fällig</th><th>Zugewiesen</th><th>{nur === "offen" ? "Erstellt" : "Erledigt"}</th></tr></thead>
                <tbody>
                  {d.aufgaben.map((a) => {
                    const ueber = !a.erledigtAt && a.faelligTag && a.faelligTag < heute;
                    return (
                      <tr key={a.id} className={ueber ? "app-zeile--offen" : undefined}>
                        <td><input type="checkbox" aria-label={`${a.titel} erledigt`} checked={!!a.erledigtAt} onChange={(e) => aktion.mutate({ art: "aendern", id: a.id, daten: { erledigt: e.target.checked } })} /></td>
                        <td><span className="sc-strong">{a.titel}</span>{a.text ? <div className="sc-muted sc-small">{a.text}</div> : null}</td>
                        <td><StatusBadge status={a.art === "manuell" ? "neutral" : "wait"}>{ART_TEXT[a.art] ?? a.art}</StatusBadge></td>
                        <td>{a.faelligTag ? <span className={ueber ? "sc-strong" : undefined}>{formatTag(a.faelligTag)}{ueber ? " · überfällig" : ""}</span> : "–"}</td>
                        <td>
                          <select className="sc-select" aria-label="Zuweisen" value={a.zugewiesenBenutzerId ?? ""} onChange={(e) => aktion.mutate({ art: "aendern", id: a.id, daten: { zugewiesenBenutzerId: e.target.value || null } })}>
                            <option value="">Niemand</option>
                            {personen.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
                          </select>
                        </td>
                        <td className="sc-muted sc-small">{formatDatumZeit(a.erledigtAt ?? a.erstelltAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Seitenzustand>
      <Dialog open={offen} title="Neue Aufgabe" size="md" onClose={() => setOffen(false)} actions={<><Button variant="quiet" onClick={() => setOffen(false)}>Abbrechen</Button><Button variant="primary" loading={aktion.isPending} onClick={(e) => void anlegen(e as unknown as FormEvent)}>Anlegen</Button></>}>
        <form className="app-form" onSubmit={(e) => void anlegen(e)} noValidate>
          <FormGrid>
            <TextField label="Was ist zu tun?" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} autoFocus maxLength={120} />
            <TextField label="Details" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} maxLength={500} />
            <Select label="Zuweisen" value={form.zugewiesenBenutzerId} onChange={(e) => setForm({ ...form, zugewiesenBenutzerId: e.target.value })}>
              <option value="">Niemand</option>
              {personen.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
            </Select>
            <TextField label="Fällig" type="date" value={form.faelligTag} onChange={(e) => setForm({ ...form, faelligTag: e.target.value })} />
          </FormGrid>
        </form>
      </Dialog>
    </>
  );
}
